import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ColumnFiltersState, OnChangeFn } from "@tanstack/react-table";
import { AlertCircle, Loader2 } from "lucide-react";
import { z } from "zod";
import { getEtpaOrdersAutoTraderEtpaOrdersGet, getOrdersSu2mAutoTraderDbOrdersGet } from "@/client-admin";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AUTO_TRADER_WORKER_STATUS_KEY,
  AutoTraderWorkerControls,
  useAutoTraderWorkerStatus,
} from "@/components/admin/AutoTraderWorkerControls";
import { EtpaOrdersTable } from "@/components/admin/EtpaOrdersTable/EtpaOrdersTable";
import { SumOrdersTable } from "@/components/admin/SumOrdersTable/SumOrdersTable";
import { StaleQueryAlertComponent } from "@/components/StaleQueryAlertComponent";
import { GRAFANA_AUTO_TRADER_LOGS_URL, GRAFANA_AUTO_TRADER_MONITORING_URL } from "@/App";
import { checkSuperUserPermission, getErrorDetails } from "@/lib/utils";

const filterParam = z.string().min(1).optional().catch(undefined);

const autoTraderMonitoringSearchSchema = z.object({
  tab: z.enum(["sum-orders", "etpa-orders"]).catch("sum-orders"),
  sumIntradayCode: filterParam,
  sumDate: filterParam,
  sumStatusBCI: filterParam,
  sumOrderType: filterParam,
  etpaId: filterParam,
  etpaIntradayCode: filterParam,
  etpaOrderType: filterParam,
  etpaOrderStatus: filterParam,
  etpaXbidOrderType: filterParam,
});

type AutoTraderMonitoringSearch = z.infer<typeof autoTraderMonitoringSearchSchema>;

type FilterParamSpec = {
  columnId: string;
  param: Exclude<keyof AutoTraderMonitoringSearch, "tab">;
  isList: boolean;
};

const sumFilterSpecs: FilterParamSpec[] = [
  { columnId: "intradayCode", param: "sumIntradayCode", isList: true },
  { columnId: "date", param: "sumDate", isList: true },
  { columnId: "statusBCI", param: "sumStatusBCI", isList: true },
  { columnId: "orderType", param: "sumOrderType", isList: true },
];

const etpaFilterSpecs: FilterParamSpec[] = [
  { columnId: "id", param: "etpaId", isList: false },
  { columnId: "intradayCode", param: "etpaIntradayCode", isList: true },
  { columnId: "orderType", param: "etpaOrderType", isList: true },
  { columnId: "orderStatus", param: "etpaOrderStatus", isList: true },
  { columnId: "xbidOrderType", param: "etpaXbidOrderType", isList: true },
];

// The statusBCI facet has an "Empty" option whose value is null; encode it as the literal string "null".
function searchToColumnFilters(search: AutoTraderMonitoringSearch, specs: FilterParamSpec[]): ColumnFiltersState {
  return specs.flatMap((spec): ColumnFiltersState => {
    const paramValue = search[spec.param];
    if (!paramValue) return [];
    if (!spec.isList) return [{ id: spec.columnId, value: paramValue }];
    return [{ id: spec.columnId, value: paramValue.split(",").map((item) => (item === "null" ? null : item)) }];
  });
}

function columnFiltersToSearch(
  filters: ColumnFiltersState,
  specs: FilterParamSpec[],
): Partial<AutoTraderMonitoringSearch> {
  return Object.fromEntries(
    specs.map((spec) => {
      const filterValue = filters.find((filter) => filter.id === spec.columnId)?.value;
      const paramValue = spec.isList
        ? (filterValue as (string | null)[] | undefined)?.map((item) => (item === null ? "null" : item)).join(",")
        : (filterValue as string | undefined);
      return [spec.param, paramValue || undefined];
    }),
  );
}

export const Route = createFileRoute("/_superuser/admin/auto-trader-monitoring")({
  component: AutoTraderMonitoringDashboard,
  validateSearch: autoTraderMonitoringSearchSchema,
  beforeLoad: () => {
    checkSuperUserPermission("auto-trader");
  },
});

function AutoTraderMonitoringDashboard() {
  const search = Route.useSearch();
  const { tab } = search;
  const navigate = useNavigate({ from: Route.fullPath });

  const sumFilters = searchToColumnFilters(search, sumFilterSpecs);
  const etpaFilters = searchToColumnFilters(search, etpaFilterSpecs);

  const handleFilterChange =
    (specs: FilterParamSpec[], currentFilters: ColumnFiltersState): OnChangeFn<ColumnFiltersState> =>
    (updater) => {
      const nextFilters = typeof updater === "function" ? updater(currentFilters) : updater;
      navigate({
        search: (previous) => ({ ...previous, ...columnFiltersToSearch(nextFilters, specs) }),
        replace: true,
      });
    };

  const { data: workerStatusData, isLoading: isWorkerStatusLoading } = useAutoTraderWorkerStatus();

  const { data: dbOrdersData, isLoading: isDbOrdersLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: () => getOrdersSu2mAutoTraderDbOrdersGet(),
    refetchInterval: (query) => {
      return query.state.status === "error" ? 5000 : 10000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });

  const {
    data: etpaOrdersData,
    isLoading: isEtpaOrdersLoading,
    error: etpaOrdersError,
  } = useQuery({
    queryKey: ["etpa-orders"],
    queryFn: () => getEtpaOrdersAutoTraderEtpaOrdersGet(),
    refetchInterval: (query) => {
      return query.state.status === "error" ? 5000 : 10000;
    },
    refetchIntervalInBackground: true,
    retry: false,
  });

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold tracking-tight">Auto Trader Status</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-1 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Auto Trader Worker status</CardTitle>
              <AutoTraderWorkerControls />
            </div>
          </CardHeader>
          <CardContent>
            {isWorkerStatusLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <div className="mb-1 text-xs">Status: {workerStatusData?.status ?? "No data"}</div>
                <div className="mb-1 text-xs">
                  Last portal state command: {workerStatusData?.latest_portal_command ?? "-"}
                  {workerStatusData?.latest_portal_command_at
                    ? ` (${new Date(workerStatusData.latest_portal_command_at + "Z").toLocaleString()})`
                    : ""}
                </div>
                <div className="text-xs">
                  Last auto trader state acknowledgement:{" "}
                  {workerStatusData?.created_at ? new Date(workerStatusData.created_at + "Z").toLocaleString() : "-"}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <h2 className="text-lg font-semibold tracking-tight">Grafana Auto Trader Observability</h2>
      <div className="flex gap-2">
        <a
          href={GRAFANA_AUTO_TRADER_MONITORING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonVariants({ variant: "outline" })}
        >
          Open Grafana Monitoring Dashboard
        </a>
        <a
          href={GRAFANA_AUTO_TRADER_LOGS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={buttonVariants({ variant: "outline" })}
        >
          Open Grafana Logs
        </a>
      </div>

      <h2 className="text-lg font-semibold tracking-tight">Orders</h2>
      <StaleQueryAlertComponent
        queries={[
          { key: AUTO_TRADER_WORKER_STATUS_KEY, name: "Worker Status" },
          { key: ["orders"], name: "Su2m Orders" },
          { key: ["etpa-orders"], name: "ETPA Orders" },
        ]}
        stalenessThresholdMs={60000}
      />
      {etpaOrdersData?.data.length === 0 && !etpaOrdersError && (
        <Alert variant="destructive">
          <AlertDescription>⚠️ WARNING: No open orders in ETPA</AlertDescription>
        </Alert>
      )}
      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate({
            search: (previous) => ({ ...previous, tab: value as "sum-orders" | "etpa-orders" }),
            replace: true,
          })
        }
        className="w-full"
      >
        <TabsList>
          <TabsTrigger value="sum-orders">Sum Orders</TabsTrigger>
          <TabsTrigger value="etpa-orders" className="relative">
            ETPA Orders
            {etpaOrdersError && <AlertCircle className="ml-2 h-4 w-4 text-destructive" />}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="sum-orders">
          {isDbOrdersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <SumOrdersTable
              rows={dbOrdersData ? dbOrdersData.data : []}
              columnFilters={sumFilters}
              onColumnFiltersChange={handleFilterChange(sumFilterSpecs, sumFilters)}
            />
          )}
        </TabsContent>
        <TabsContent value="etpa-orders" className="flex flex-col gap-2">
          {etpaOrdersError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                <div className="font-semibold">
                  Error fetching ETPA orders{etpaOrdersData ? " — showing last loaded data" : ""}
                </div>
                <div className="mt-2 text-sm">{getErrorDetails(etpaOrdersError, true)}</div>
              </AlertDescription>
            </Alert>
          )}
          {isEtpaOrdersLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : etpaOrdersData ? (
            <EtpaOrdersTable
              rows={etpaOrdersData.data}
              columnFilters={etpaFilters}
              onColumnFiltersChange={handleFilterChange(etpaFilterSpecs, etpaFilters)}
            />
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  );
}
